import { useState, type RefObject } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { downloadBlob } from "@/components/ToolLayout";
import { elementToBleedPdfBytes } from "@/lib/printExport";
import { useExportGate } from "@/hooks/useExportGate";

/** "Print shop PDF": trim size + 0.125" bleed on every side, TrimBox/BleedBox set. */
export function PrintShopPdfButton({ target, filename, defaultWidthIn = 5 }: { target: RefObject<HTMLElement | null>; filename: string; defaultWidthIn?: number }) {
  const [width, setWidth] = useState(String(defaultWidthIn));
  const [busy, setBusy] = useState(false);
  const { checkExport } = useExportGate();
  const go = async () => {
    const w = parseFloat(width.replace(",", "."));
    if (!target.current) return;
    if (!(w >= 2 && w <= 24)) { toast.error("Enter the finished (cut) width in inches, between 2 and 24"); return; }
    setBusy(true);
    try {
      if (!(await checkExport())) return;
      const bytes = await elementToBleedPdfBytes(target.current, w);
      downloadBlob(new Blob([bytes as BlobPart], { type: "application/pdf" }), filename);
      toast.success("Print-shop PDF downloaded");
    } catch (e) { console.error(e); toast.error("Couldn't build the print-shop PDF"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm">
        <label htmlFor="trim-width" className="whitespace-nowrap">Finished width (in)</label>
        <input id="trim-width" inputMode="decimal" value={width} onChange={(e) => setWidth(e.target.value)} className="h-8 w-20 rounded-md border bg-background px-2 text-sm" />
      </div>
      <Button className="w-full" variant="outline" onClick={go} disabled={busy}>{busy ? "Building…" : 'Download print-shop PDF (0.125" bleed)'}</Button>
      <p className="text-xs text-muted-foreground">Page = finished size + 0.125" on every side. The design's edge is extended into the bleed so there's no white sliver after cutting; keep text at least 0.25" inside the edge.</p>
    </div>
  );
}
